/**
 * Per-`HookCode` `params` validation, applied at the transition-hook admin write
 * boundary. Kept out of `workflow.schema.ts` because it grows per hook code
 * while the storage schemas around it do not.
 */
import { EMIT_EVENT_ALLOWED, type EventType, HookCode, RequiredWorkOrderField } from '@atlas/types'
import { z } from 'zod'

const workflowTextSchema = z.string().min(1)

/**
 * Upper bound on `log_message`'s delay. Deliberately below the hook worker's
 * Lambda timeout (`WORK_ORDER_HOOK_WORKER_TIMEOUT_SECONDS`, 30s) with room for
 * the configuration read and a cold start, so a delay this schema accepts can
 * always finish inside one invocation.
 */
export const MAX_LOG_MESSAGE_DELAY_SECONDS = 20

const checklistCompleteParamsSchema = z
	.object({
		checklistTemplateCode: workflowTextSchema.optional(),
		mode: z.literal('dynamic').optional(),
	})
	.strict()
	.refine((v) => v.checklistTemplateCode !== undefined || v.mode === 'dynamic', {
		message: 'Either checklistTemplateCode or mode="dynamic" is required',
	})

/** Codes with no parameters - presence of ANY key is rejected. */
const emptyParamsSchema = z.object({}).strict()

/**
 * `emit_event` may only name an event type the hook worker is allowed to
 * publish. `EMIT_EVENT_ALLOWED` is currently empty, so every write is
 * rejected; the message says so rather than reporting an enum mismatch
 * against an empty set, which reads as a bug (ADR-0024).
 */
const emitEventParamsSchema = z
	.object({ eventType: workflowTextSchema })
	.strict()
	.superRefine((value, ctx) => {
		if (EMIT_EVENT_ALLOWED.includes(value.eventType as EventType)) return
		ctx.addIssue({
			code: 'custom',
			message:
				EMIT_EVENT_ALLOWED.length === 0
					? "No event type is publishable from a transition hook yet, so 'emit_event' cannot be attached"
					: `eventType must be one of: ${EMIT_EVENT_ALLOWED.join(', ')}`,
			path: ['eventType'],
		})
	})

/**
 * `log_message` is the diagnostic hook: a message, and an optional delay before
 * it is written.
 *
 * The delay is what makes it a useful probe rather than a toy. A post-hook that
 * sleeps and then logs cannot possibly have completed inside the request that
 * committed the transition, so seeing the line at all proves the work moved to
 * its own invocation.
 *
 * `maxDelaySeconds` is bounded well below the worker's Lambda timeout on
 * purpose: a delay at or past the timeout guarantees the invocation is killed
 * mid-hook, retried, and finally dead-lettered, which reads as a broken hook
 * system rather than as the correct timeout it is. Rejecting it here means the
 * mistake surfaces as a validation error on the write instead.
 */
const logMessageParamsSchema = z
	.object({
		message: workflowTextSchema.max(200),
		delaySeconds: z.number().int().min(0).max(MAX_LOG_MESSAGE_DELAY_SECONDS).optional(),
	})
	.strict()

/** `fields_present`: the fields the order must have, at least one, each named once. */
const fieldsPresentParamsSchema = z
	.object({
		fields: z
			.array(z.enum(RequiredWorkOrderField))
			.min(1)
			.refine((fields) => new Set(fields).size === fields.length, { message: 'fields must not repeat a field' }),
	})
	.strict()

/** Per-`HookCode` params validation, applied at the transition-hook admin write boundary. Every code is covered. */
export const hookParamsSchemaByCode: Partial<Record<HookCode, z.ZodType>> = {
	[HookCode.CHECKLIST_COMPLETE]: checklistCompleteParamsSchema,
	[HookCode.DELIVERY_SLOTS_FILLED]: z.object({ slot: workflowTextSchema }).strict(),
	[HookCode.ARTIFACT_EXISTS]: z.object({ artifact: workflowTextSchema }).strict(),
	[HookCode.CHANGE_REQUEST_CREATED]: emptyParamsSchema,
	[HookCode.CHANGE_REQUEST_RESOLVED]: emptyParamsSchema,
	[HookCode.ASSIGNEE_PRESENT]: emptyParamsSchema,
	[HookCode.FIELDS_PRESENT]: fieldsPresentParamsSchema,
	[HookCode.NOTIFY]: z.object({ url: z.url() }).strict(),
	[HookCode.EMIT_EVENT]: emitEventParamsSchema,
	[HookCode.LOG_MESSAGE]: logMessageParamsSchema,
}
